import { createStore, createEffect, createEvent, combine } from 'effector';
import i18n from 'i18n';
import { $notificationStore } from '../notificationStore';
import { AccountsApi, IntegrationsApi } from '@shared/api/rest';
import { ApiUtils } from '@shared/utils';
import { type IQueryParams, RequestStrategyEnum, type ApiPaginate } from '@shared/api/models';
import type { AccountForIntegrationsRequest, AccountForIntegrationsResponse } from '@shared/api/rest/accounts';
import type { DisconnectAccountRequest, SetAccountTypeRequest } from '@shared/api/rest/integrations';

const getAccountsEffect = createEffect((params: IQueryParams<AccountForIntegrationsRequest>) =>
  AccountsApi.getAccountsForIntegrations(params.request, params?.signal),
);
const connectAccountsEffect = createEffect((params: IQueryParams<SetAccountTypeRequest>) =>
  IntegrationsApi.setAccountType(params.request, params?.signal),
);
const disconnectAccountEffect = createEffect((params: IQueryParams<DisconnectAccountRequest>) =>
  IntegrationsApi.disconnectAccount(params.request, params?.signal),
);
const clearEvent = createEvent();

const $dataStore = createStore<ApiPaginate<AccountForIntegrationsResponse[]> | null>(null)
  .on(getAccountsEffect.done, (current, data) => data.result ?? current)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getAccountsEffect.pending, (_, data) => data)
  .on(getAccountsEffect.fail, () => false)
  .on(getAccountsEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getAccountsEffect, () => null)
  .on(getAccountsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getAccountsEffect.done, () => null)
  .on(connectAccountsEffect, () => null)
  .on(connectAccountsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(connectAccountsEffect.done, () => null)
  .on(disconnectAccountEffect, () => null)
  .on(disconnectAccountEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(disconnectAccountEffect.done, () => null)
  .reset(clearEvent);
$errorStore.watch((x) => $notificationStore.events.setError(x));

export const $accountForIntegrationsStore = {
  events: {
    getAccounts: ApiUtils.wrapperApi(getAccountsEffect, RequestStrategyEnum.Debounce),
    disconnectAccount: ApiUtils.wrapperApi(disconnectAccountEffect, RequestStrategyEnum.Default),
    connectAccounts: ApiUtils.wrapperApi(connectAccountsEffect, RequestStrategyEnum.Default),
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
